
import { useState, useEffect } from 'react';
import { useBackgroundParallax } from '../../../hooks/useParallax';

export default function HeroSection() {
  const scrollY = useBackgroundParallax(0.4);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] overflow-hidden" aria-label="Главный экран GA HR Group">
      {/* Parallax Background Elements */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{ transform: `translateY(${scrollY * 0.3}px)` }}
      >
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 right-20 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-3xl"></div>
      </div>

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 w-full flex items-center pt-20 pb-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full">
          {/* Left Content */}
          <div 
            className="space-y-5 sm:space-y-6 text-center lg:text-left order-1 lg:order-1"
            style={{ transform: `translateY(${scrollY * 0.15}px)` }}
          >
            <div 
              className={`inline-block transition-all duration-700 ease-out ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: '200ms' }}
            >
              <span className="px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#D4AF37] text-xs sm:text-sm font-medium">
                HR-директор на аутсорсе
              </span>
            </div>
            
            <h1 
              className={`text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight transition-all duration-700 ease-out ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              <strong>GA HR GROUP-Управление персоналом</strong>
              <span className="block text-[#D4AF37] mt-2">как работающий механизм</span>
            </h1>
            
            <p 
              className={`text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed max-w-lg mx-auto lg:mx-0 transition-all duration-700 ease-out ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: '600ms' }}
            >
              Я строю <strong>системы</strong>, которые экономят миллионы и возвращают собственнику время.
            </p>

            {/* Mobile-only image — between text and buttons */}
            <div 
              className={`block lg:hidden relative w-full h-[350px] sm:h-[420px] transition-all duration-1000 ease-out ${
                isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}
              style={{ transitionDelay: '500ms' }}
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-[#D4AF37]/20 to-transparent rounded-3xl blur-3xl"></div>
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src="https://compme.ru/images/Angelina.png"
                  alt="GA HR Group — HR-бизнес-партнёр Ангелина Горбатовская, аутсорсинг HR-директора"
                  title="GA HR Group — профессиональное управление персоналом"
                  className="w-full h-full object-contain z-10 scale-110"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            
            <div 
              className={`flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start transition-all duration-700 ease-out ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: '800ms' }}
            >
              <a 
                href="#contact" 
                className="px-6 sm:px-8 py-3 sm:py-4 bg-[#D4AF37] text-black font-semibold rounded-lg hover:bg-[#B8941F] transition-all duration-300 shadow-lg hover:shadow-[#D4AF37]/50 hover:scale-105 whitespace-nowrap cursor-pointer text-center text-sm sm:text-base"
              >
                Получить консультацию
              </a>
              <a 
                href="#services" 
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white/5 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-white/20 whitespace-nowrap cursor-pointer text-center text-sm sm:text-base"
              >
                Наши услуги
              </a>
            </div>
            
            <div 
              className={`grid grid-cols-3 gap-4 sm:gap-6 pt-5 sm:pt-6 border-t border-white/10 transition-all duration-700 ease-out ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: '1000ms' }}
            >
              <div className="group">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#D4AF37] group-hover:scale-110 transition-transform duration-300">15+</div>
                <div className="text-xs sm:text-sm text-gray-400 mt-1">лет опыта</div>
              </div>
              <div className="group">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#D4AF37] group-hover:scale-110 transition-transform duration-300">200+</div>
                <div className="text-xs sm:text-sm text-gray-400 mt-1">проектов</div>
              </div>
              <div className="group">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#D4AF37] group-hover:scale-110 transition-transform duration-300">95%</div>
                <div className="text-xs sm:text-sm text-gray-400 mt-1">довольных клиентов</div>
              </div>
            </div>
          </div>

          {/* Right Image — desktop only */}
          <div 
            className={`relative hidden lg:flex items-center justify-center order-2 lg:order-2 h-[85vh] transition-all duration-1000 ease-out ${
              isLoaded ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-12 scale-95'
            }`}
            style={{ 
              transform: `translateY(${scrollY * -0.2}px)`,
              transitionDelay: '600ms'
            }}
          >
            <div 
              className="absolute inset-0 bg-gradient-to-tr from-[#D4AF37]/20 to-transparent rounded-3xl blur-3xl"
              style={{ transform: `translateY(${scrollY * 0.1}px)` }}
            ></div>
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src="https://compme.ru/images/Angelina.png"
                alt="GA HR Group — HR-бизнес-партнёр Ангелина Горбатовская, аутсорсинг HR-директора"
                title="GA HR Group — профессиональное управление персоналом"
                className="w-full h-full object-contain z-10 scale-110"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
